import React from "react";
import { Check, ChevronsUpDown, Download, Search, X } from "lucide-react";
import { cn } from "lib/utils";
import { Button } from "components/ui/button";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "components/ui/popover";
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
} from "components/ui/command";
import { AccountabilityPerson, RuleKey } from "../api/api";

interface Props {
    search: string;
    redRules: Set<RuleKey>;
    total: number;
    onSearchChange: (val: string) => void;
    onToggleRule: (rule: RuleKey) => void;
    onClearRules: () => void;
    showDownloadButton?: boolean;
    downloadingExcel?: boolean;
    onDownloadExcel?: () => void;
    accountabilityPeople: AccountabilityPerson[];
    selectedAccountability: string;
    onAccountabilityChange: (ecode: string) => void;
    disableAccountabilityDropdown?: boolean;
}

const RULE_LABELS: Array<{ key: RuleKey; label: string }> = [
    { key: "MRR", label: "MRR" },
    { key: "CM", label: "CM%" },
    { key: "OVERDUE_60D", label: "Overdue >60d" },
    { key: "UNBILLED_EXCL_STOCK", label: "Unbilled" },
    { key: "FRAGMENTATION", label: "Fragmentation" },
    { key: "CREDIT_LIMIT", label: "Exposure" },
];

const HealthHeader: React.FC<Props> = ({
    search,
    redRules,
    total,
    onSearchChange,
    onToggleRule,
    onClearRules,
    showDownloadButton = false,
    downloadingExcel = false,
    onDownloadExcel,
    accountabilityPeople,
    selectedAccountability,
    onAccountabilityChange,
    disableAccountabilityDropdown = false,
}) => {
    const hasActive = redRules.size > 0;
    const [accountabilityOpen, setAccountabilityOpen] = React.useState(false);
    const selectedPerson = accountabilityPeople.find(p => p.ecode === selectedAccountability);

    return (
        <div className="flex flex-col gap-3 w-full">
            <div className="flex items-end justify-between w-full gap-4 flex-wrap">
                <div className="flex items-end gap-3 flex-wrap">
                    <div className="min-w-0 w-[320px]">
                        <label className="block text-sm font-medium mb-1 text-gray-700">
                            Search Customer
                        </label>
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground h-4 w-4" />
                            <input
                                type="text"
                                value={search}
                                onChange={(e) => onSearchChange(e.target.value)}
                                placeholder="Search by customer name or code..."
                                className="w-full h-10 rounded-md border border-input bg-background pl-9 pr-3 text-sm focus:outline-none focus:ring-1 focus:ring-ring"
                            />
                        </div>
                    </div>

                    <div className="min-w-0 w-[260px]">
                        <label className="block text-sm font-medium mb-1 text-gray-700">
                            Final Accountability
                        </label>
                        <Popover open={accountabilityOpen} onOpenChange={setAccountabilityOpen}>
                            <PopoverTrigger asChild>
                                <Button
                                    variant="outline"
                                    role="combobox"
                                    disabled={disableAccountabilityDropdown}
                                    className="w-full justify-between h-10 px-2.5 text-sm truncate"
                                >
                                    {selectedPerson ? selectedPerson.name : "-- select --"}
                                    <ChevronsUpDown className="ml-2 h-4 w-4 opacity-50" />
                                </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-[260px] p-0">
                                <Command>
                                    <CommandInput placeholder="Search user..." />
                                    <CommandEmpty>No user found.</CommandEmpty>
                                    <CommandGroup className="max-h-[300px] overflow-y-auto">
                                        {accountabilityPeople.map((p) => (
                                            <CommandItem
                                                key={p.ecode}
                                                value={p.name}
                                                onSelect={() => {
                                                    const newValue = selectedAccountability === p.ecode ? "" : p.ecode;
                                                    onAccountabilityChange(newValue);
                                                    setAccountabilityOpen(false);
                                                }}
                                            >
                                                <Check
                                                    className={cn(
                                                        "mr-2 h-4 w-4",
                                                        selectedAccountability === p.ecode ? "opacity-100" : "opacity-0"
                                                    )}
                                                />
                                                <span className="block max-w-[200px] truncate" title={p.name}>
                                                    {p.name}
                                                </span>
                                            </CommandItem>
                                        ))}
                                    </CommandGroup>
                                </Command>
                            </PopoverContent>
                        </Popover>
                    </div>
                </div>

                <div className="text-sm text-gray-600 pb-2.5">
                    <strong>{total}</strong> customer{total === 1 ? "" : "s"}
                    {hasActive && " matching filters"}
                </div>
            </div>

            <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm text-gray-600 mr-1">Show only red:</span>

                    {RULE_LABELS.map(({ key, label }) => {
                        const active = redRules.has(key);
                        return (
                            <button
                                key={key}
                                type="button"
                                onClick={() => onToggleRule(key)}
                                className={
                                    "inline-flex items-center gap-1.5 px-3 py-1 rounded-full border text-sm transition-colors " +
                                    (active
                                        ? "border-red-500 bg-red-50 text-red-700 hover:bg-red-100"
                                        : "border-gray-300 bg-white text-gray-700 hover:bg-gray-50")
                                }
                            >
                                <span
                                    className={
                                        "inline-block h-2 w-2 rounded-full " +
                                        (active ? "bg-red-500" : "bg-gray-300")
                                    }
                                />
                                {label}
                            </button>
                        );
                    })}

                    {hasActive && (
                        <button
                            type="button"
                            onClick={onClearRules}
                            className="inline-flex items-center gap-1 px-2 py-1 text-xs text-gray-600 hover:text-gray-900"
                        >
                            <X size={14} />
                            Clear
                        </button>
                    )}
                </div>

                {showDownloadButton && (
                    <button
                        type="button"
                        onClick={onDownloadExcel}
                        disabled={downloadingExcel}
                        className="inline-flex items-center gap-1.5 px-3 py-1 text-sm rounded-md border border-green-600 text-green-700 hover:bg-green-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        <Download size={16} />
                        {downloadingExcel ? "Downloading..." : "Excel"}
                    </button>
                )}
            </div>
        </div>
    );
};

export default HealthHeader;
